// Enterprise Layout Component
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth';
import type { User } from '../../types/auth';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const getUserDisplayName = () => {
    if (!user) return 'User';
    
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.username) {
      return user.username;
    } else {
      return user.email?.split('@')[0] || 'User';
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      authService.clearAuthData();
      navigate('/login');
    }
  };

  const isActivePage = (path: string) => {
    return location.pathname === path;
  };

  if (loading) {
    return (
      <div className="page-theme-list">
        <div className="main-content">
          <div className="content-container">
            <div className="loading-state">
              <h2>Loading...</h2>
              <p>Please wait while we prepare your workspace.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-theme-list">
      {/* Global Navigation */}
      <header className="header-navbar">
        <div className="navbar-content">
          <div className="navbar-brand">
            <img src="/img/icon-f.png" alt="Suvadi" className="brand-logo" />
            <span className="brand-text">
              <span className="tamil tamil-highlight">சு</span><span className="tamil tamil-accent">வடி</span> Suvadi
            </span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="navbar-nav">
            <a 
              href="/dashboard" 
              className={`nav-item ${isActivePage('/dashboard') ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
            >
              Dashboard
            </a>
            <div className="nav-transparent-divider">
              <div className="nav-moving-ball"></div>
            </div>
            <a 
              href="/warranties" 
              className={`nav-item ${isActivePage('/warranties') ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/warranties');
              }}
            >
              Warranties
            </a>
            <div className="nav-transparent-divider">
              <div className="nav-moving-ball"></div>
            </div>
            <a 
              href="/products" 
              className={`nav-item ${isActivePage('/products') ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/products');
              }}
            >
              Products
            </a>
            <div className="nav-transparent-divider">
              <div className="nav-moving-ball"></div>
            </div>
            <a 
              href="/admin" 
              className={`nav-item ${isActivePage('/admin') ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin');
              }}
            >
              Admin
            </a>
          </nav>
          
          <div className="navbar-actions">
            <div className="navbar-welcome heritage-welcome">
              <span className="welcome-prefix">Welcome</span>
              <span className="welcome-name handwritten">{getUserDisplayName()}</span>
              <span className="welcome-suffix">✨</span>
            </div>
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/add-warranty')}
            >
              Add Warranty
            </button>
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Page Content */}
      {children}
      
      {/* Footer */}
      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/img/icon-f.png" alt="Suvadi" className="footer-logo-img" />
            <span className="brand-text">சுவடி SUVADI</span>
          </div>
          <div className="footer-links">
            <a href="/help" className="footer-link">Help</a>
            <a href="mailto:support@suvadi.com" className="footer-link">Support</a>
            <a href="/privacy" className="footer-link">Privacy</a>
          </div>
          <div className="footer-copyright">
            <p>&copy; 2025 Suvadi. Professional warranty management.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
